import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { Observable } from 'rxjs';
import { FORM_SHARED_IMPORTS } from 'src/app/shared/imports/form-shared.imports';
import { ConfigStateService } from '@abp/ng.core';
import { LocalizationPipe } from '@abp/ng.core';
import { SettingService } from 'src/core/services/settings/setting.service';
import { ConversationalAnalyticsSettingsDto } from 'src/core/models/settings/conversational-analtyics-settings/conversational-analytics-settings.dto';
import { IntegrationType } from 'src/core/models/settings/integration-type.enum';
import { SettingsTestResultDto } from 'src/core/models/settings/settings-test-result.dto';

@Component({
  selector: 'app-conversational-analytics-settings',
  standalone: true,
  templateUrl: './conversational-analytics-settings.component.html',
  styleUrls: ['../integration-settings-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...FORM_SHARED_IMPORTS, LocalizationPipe],
})
export class ConversationalAnalyticsSettingsComponent extends BaseFormComponent<ConversationalAnalyticsSettingsDto> {
  private settingService = inject(SettingService);
  private configStateService = inject(ConfigStateService);

  isTesting = false;
  private activeSyncInProgress = false;

  @Input() set hasActiveSync(value: boolean) {
    this.activeSyncInProgress = value;
    this.updateFormState();
    this.cdr.markForCheck();
  }

  get hasActiveSync(): boolean {
    return this.activeSyncInProgress;
  }

  constructor() {
    super(inject(ChangeDetectorRef));
  }

  protected buildForm(): FormGroup {
    return new FormGroup({
      apiBaseUrl: new FormControl<string>('', {
        nonNullable: true,
        validators: this.httpsUrlValidators(),
      }),
      identityBaseUrl: new FormControl<string>('', {
        nonNullable: true,
        validators: this.httpsUrlValidators(),
      }),
      scope: new FormControl<string>('', { nonNullable: true }),
      clientId: new FormControl<string>('', { nonNullable: true }),
      clientSecret: new FormControl<string>('', { nonNullable: true }),
    });
  }

  protected override afterFormInit(): void {
    this.loadSettings();
    this.updateFormState();
  }

  protected submitRequest(value: ConversationalAnalyticsSettingsDto): Observable<unknown> {
    const mapping = {
      apiBaseUrl: 'ConversationalAnalytics.ApiBaseUrl',
      identityBaseUrl: 'ConversationalAnalytics.IdentityBaseUrl',
      scope: 'ConversationalAnalytics.Scope',
      clientId: 'ConversationalAnalytics.ClientId',
      clientSecret: 'ConversationalAnalytics.ClientSecret',
    };

    const allSettings = this.convertToSettingsDto(value, mapping);
    const settings = allSettings.filter(dto => {
      const val = dto.settingValue;
      if (typeof val === 'string') {
        return val.trim() !== '';
      }
      return val != null;
    });
    return this.settingService.saveIntegration({
      integrationType: IntegrationType.ConversationalAnalytics,
      settings,
    });
  }

  private loadSettings(): void {
    const apiBaseUrl =
      this.configStateService.getSetting('ConversationalAnalytics.ApiBaseUrl') || '';
    const identityBaseUrl =
      this.configStateService.getSetting('ConversationalAnalytics.IdentityBaseUrl') || '';
    const scope = this.configStateService.getSetting('ConversationalAnalytics.Scope') || '';
    const clientId = this.configStateService.getSetting('ConversationalAnalytics.ClientId') || '';

    this.setValue({
      apiBaseUrl,
      identityBaseUrl,
      scope,
      clientId,
    });
  }

  testConnection(): void {
    this.isTesting = true;
    this.cdr.markForCheck();

    this.settingService.testSettingsConnection().subscribe({
      next: (response: SettingsTestResultDto) => {
        this.isTesting = false;
        if (response.result) {
          this.toasterService.success(response.message);
        } else {
          this.toasterService.error(response.message);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isTesting = false;
        this.cdr.markForCheck();
      },
    });
  }

  private updateFormState(): void {
    if (!this.form) {
      return;
    }

    if (this.activeSyncInProgress) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
  }
}
